/**
 * MacroBadge — compact horizontal row of macro values.
 *
 * Renders calories + protein + carbs + fat as four flex-wrap pills, so a
 * narrow viewport (or a long German label) wraps to multiple rows instead
 * of overflowing. Numbers use `tabular-nums` so digits line up vertically
 * across stacked badges in a list.
 *
 * Two label modes: full ("Protein") and compact ("P"/"E"/"P" depending
 * on locale). Use compact in tight card bodies, full in summaries.
 */
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { formatCalories, formatGrams } from '../../utils/format'

export default function MacroBadge({
  calories = 0,
  protein = 0,
  carbs = 0,
  fat = 0,
  compact = false,
  className = '',
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'en'

  const kcalUnit = t('nutrition.units.kcal')
  const gUnit = t('nutrition.units.g')

  const proteinLabel = compact
    ? t('nutrition.macros.short.protein')
    : t('nutrition.macros.protein')
  const carbsLabel = compact
    ? t('nutrition.macros.short.carbs')
    : t('nutrition.macros.carbs')
  const fatLabel = compact
    ? t('nutrition.macros.short.fat')
    : t('nutrition.macros.fat')

  const proteinFull = t('nutrition.macros.protein')
  const carbsFull = t('nutrition.macros.carbs')
  const fatFull = t('nutrition.macros.fat')
  const caloriesFull = t('nutrition.macros.calories')

  const items = [
    {
      key: 'calories',
      value: formatCalories(calories, locale),
      unit: kcalUnit,
      label: '',
      ariaLabel: `${formatCalories(calories, locale)} ${kcalUnit} ${caloriesFull}`,
    },
    {
      key: 'protein',
      value: formatGrams(protein, locale),
      unit: gUnit,
      label: proteinLabel,
      ariaLabel: `${formatGrams(protein, locale)} ${gUnit} ${proteinFull}`,
    },
    {
      key: 'carbs',
      value: formatGrams(carbs, locale),
      unit: gUnit,
      label: carbsLabel,
      ariaLabel: `${formatGrams(carbs, locale)} ${gUnit} ${carbsFull}`,
    },
    {
      key: 'fat',
      value: formatGrams(fat, locale),
      unit: gUnit,
      label: fatLabel,
      ariaLabel: `${formatGrams(fat, locale)} ${gUnit} ${fatFull}`,
    },
  ]

  return (
    <div
      role="list"
      className={clsx(
        'flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-700',
        className
      )}
    >
      {items.map((item) => (
        <span
          key={item.key}
          role="listitem"
          aria-label={item.ariaLabel}
          className="whitespace-nowrap tabular-nums"
        >
          <span>{item.value}</span>
          <span className="text-gray-500"> {item.unit}</span>
          {item.label && (
            <span className="ml-1 text-gray-600">{item.label}</span>
          )}
        </span>
      ))}
    </div>
  )
}
